import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Clock, BookOpen, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { itinerarios } from "@/data/itinerarios";
import ItinerarioTimeline from "@/components/ItinerarioTimeline";
import Footer from "@/components/Footer";

const ItinerarioDetail = () => {
  const { itinerarioId } = useParams();
  const navigate = useNavigate();

  const itinerario = itinerarios.find(i => i.id === itinerarioId);

  if (!itinerario) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-light">Itinerario no encontrado</h1>
          <Button variant="outline" onClick={() => navigate("/cursos")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Ver todos los itinerarios
          </Button>
        </div>
      </div>
    );
  }

  const totalArticles = itinerario.steps.reduce(
    (acc, step) => acc + step.articleIds.length,
    0
  );

  const scrollToTimeline = () => {
    document.getElementById("timeline")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          <button
            onClick={() => navigate("/cursos")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Todos los itinerarios
          </button>

          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <itinerario.icon className="h-10 w-10 text-primary" />
              <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                Gratis
              </Badge>
              <Badge variant="outline">{itinerario.difficulty}</Badge>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-foreground tracking-tight leading-tight">
              {itinerario.title}
            </h1>

            <p className="text-xl md:text-2xl font-light text-muted-foreground leading-relaxed max-w-3xl">
              {itinerario.description}
            </p>

            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-6 text-base text-muted-foreground">
              <span className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {itinerario.totalTime}
              </span>
              <span className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                {itinerario.steps.length} pasos · {totalArticles} artículos
              </span>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-secondary/20 rounded-2xl p-8 md:p-10 space-y-4">
            <h2 className="text-xl font-medium text-foreground">Qué vas a conseguir</h2>
            <ul className="space-y-3">
              {itinerario.benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3 text-base text-muted-foreground font-light">
                  <span className="text-primary mt-0.5">✓</span>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          {/* Dual CTA */}
          <div className="flex flex-wrap gap-4">
            <Button size="lg" onClick={scrollToTimeline} className="gap-2">
              Empezar gratis
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate(`/contacto?tipo=itinerario&ref=${encodeURIComponent(itinerario.title)}`)}
            >
              Que lo haga Globaltis IA
            </Button>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section id="timeline" className="py-20 px-4">
        <div className="max-w-4xl mx-auto space-y-12">
          <h2 className="text-3xl md:text-4xl font-light text-foreground">
            El camino paso a paso
          </h2>
          <ItinerarioTimeline steps={itinerario.steps} itinerarioTitle={itinerario.title} />
        </div>
      </section>

      {/* Target audience */}
      <section className="py-20 px-4 bg-secondary/10">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-primary" />
            <h2 className="text-2xl md:text-3xl font-light text-foreground">Para quién es esto</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {itinerario.targetAudience.map(audience => (
              <Badge key={audience} variant="outline" className="text-sm font-light px-4 py-2">
                {audience}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-foreground leading-tight">
            Hazlo tú mismo con las guías.
            <br />
            <span className="text-primary">O cuéntanos tu caso y lo hacemos por ti.</span>
          </h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" onClick={scrollToTimeline}>
              Empezar con las guías
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate(`/contacto?tipo=itinerario&ref=${encodeURIComponent(itinerario.title)}`)}
              className="gap-2"
            >
              Contactar
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ItinerarioDetail;
